<template>
	<div
		v-if="provisionOffers && !isHidden"
		class="royal-provision-wrapper"
		:class="`royal-provision-wrapper--${isRoyalProvisionActive ? 'active' : 'default'}-46`"
		@click="handleClickRoyalProvisionBox"
	>
		<div class="royal-provision">
			<div class="royal-provision__label">
				<div
					class="royal-provision__label-text"
					v-if="!isRoyalProvisionActive"
				>
					<ShrinkText
						:text="translate('event.activate')"
						:fontSize="8"
						isBold
						isUppercase
					/>
				</div>
				<Timer
					:key="royalProvisionRemainingTime"
					v-else
					class="timer"
					:time="royalProvisionRemainingTime"
					:fontSize="10"
					:hasIcon="false"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import ShrinkText from "@/components/ShrinkText.vue";
import Timer from "@/components/Timer.vue";
import { OfferAnalyticsClickSource } from "@/libs/SocketService";
import router from "@/router";
import { PaymentProvider } from "@/services/PaymentProvider";
import useShopStore from "@/store/useShopStore";
import type { ActiveProvision } from "@/store/useUserStore";
import useUserStore from "@/store/useUserStore";
import type { ShopItem } from "@/types/shop";
import { RouteNames, ShopTab } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

const userStore = useUserStore();
const shopStore = useShopStore();
const { activeProvisions, isRoyalProvisionActive } = storeToRefs(userStore);
const { provisionOffers, hiddenLeftBoxOffers } = storeToRefs(shopStore);

const royalProvisionRemainingTime = ref<number>(0);

// Calculate remaining time of the longest active provision package
const calculateRemainingTime = (): void => {
	const provisions = activeProvisions.value;

	if (!provisions || provisions.length === 0) {
		royalProvisionRemainingTime.value = 0;
		return;
	}

	let maxExpiresAt = 0;

	for (let i = 0; i < provisions.length; i++) {
		const rawExpires = provisions[i].expiresAt;
		const expiresTime = new Date(rawExpires).getTime();

		if (expiresTime > maxExpiresAt) {
			maxExpiresAt = expiresTime;
		}
	}

	const now = Date.now();
	if (maxExpiresAt === 0 || maxExpiresAt < now) {
		royalProvisionRemainingTime.value = 0;
		return;
	}

	const diffInSeconds = (maxExpiresAt - now) / 1000;
	royalProvisionRemainingTime.value = Math.max(0, Math.floor(diffInSeconds));
};

const isHidden = computed((): boolean => {
	return !!provisionOffers.value?.some((provisionOffer: ShopItem): boolean =>
		hiddenLeftBoxOffers.value.includes(provisionOffer.store_id),
	);
});

// Watch for changes in active provisions and recalculate time
watch(
	activeProvisions,
	(): void => {
		calculateRemainingTime();
	},
	{ immediate: true },
);

const handleClickRoyalProvisionBox = (): void => {
	if (activeProvisions.value.length > 0) {
		// Find provision that ends latest (farthest date in the future)
		const latestProvision = activeProvisions.value.reduce(
			(
				latest: ActiveProvision,
				current: ActiveProvision,
			): ActiveProvision => {
				return new Date(current.expiresAt).getTime() >
					new Date(latest.expiresAt).getTime()
					? current
					: latest;
			},
		);

		PaymentProvider.openPopup(latestProvision.storeId, {
			clickSource: OfferAnalyticsClickSource.LEFTBOX,
		});
	} else {
		router.push({
			name: RouteNames.SHOP,
			params: { tab: ShopTab.PREMIUM },
		});
	}
};
</script>

<style lang="scss" scoped>
.royal-provision {
	@include flex-col-end;
	padding-bottom: 0.25rem;
	width: 100%;
	height: 100%;

	&-wrapper {
		@include clickable-element;
	}

	&__label {
		width: 100%;
		height: 0.5rem;
		font-size: 0.5rem;
		@include flex-row-center;

		&-text {
			width: 100%;
			color: #fff3dd;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		:deep(.timer) {
			padding-bottom: 0.125rem;
			color: #372615;
			text-shadow: none;
		}
	}
}
</style>
